import { ContentLink, EpiBlock, EpiContent, EpiLink } from '@api/types';
import { ContentArea, SmartLink, Title } from '@components';
import { useAppSelector } from '@hooks';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, ListItemText, styled } from '@mui/material';
import { getModalState } from '@store/modal/modal.slice';
import { colors } from '@themes/colors';
import { Fragment } from 'react';
import { MultilevelMenuLinks } from './MultilevelMenuLinks';
import { useMultilevelMenuContext } from './MultilevelMenuProvider';
import { MultilevelSubMenu } from './MultilevelSubMenu';
type MultilevelDropdownProps = {
  content?: EpiContent;
  id: string;
  links?: EpiLink[];
};

type ContentItemProps = {
  block?: EpiBlock;
  shouldHighlight: (categoryCode?: string) => boolean;
  handleCategoryEnter: (categoryCode?: string) => void;
  hasNestedCategories: (categoryCode?: string) => boolean;
};

const MegaMenuContentWrapper = styled(Box)`
  position: absolute;
  display: flex;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #f0f0f0;
  z-index: 1000;
  box-shadow: 0px 8px 8px rgb(0 0 0 / 20%);
  width: fit-content;
`;

export const MultilevelMenu = ({ content, id, links }: MultilevelDropdownProps) => {
  const { isVisible } = useAppSelector(getModalState(id));
  const { handleCategoryEnter, leftStyle } = useMultilevelMenuContext();

  return (
    <MegaMenuContentWrapper
      onMouseLeave={() => handleCategoryEnter()}
      className='multilevel-megamenu'
      sx={{ left: leftStyle || 1 }}
    >
      <Box sx={{ py: 2, display: isVisible ? 'block' : 'none' }}>
        <ContentArea
          content={content}
          wrapperComponent={(props) => (
            <Title
              variant='h3'
              sx={{
                px: 6,
              }}
            >
              {props.block.menuTitle}
            </Title>
          )}
        />
        <ContentArea content={content} wrapperComponent={(props) => <ContentItem {...props} />} />
        <MultilevelMenuLinks links={links} />
      </Box>
      <MultilevelSubMenu isVisible={isVisible} content={content} />
    </MegaMenuContentWrapper>
  );
};

const ContentItem = ({ block }: ContentItemProps) => {
  const { handleCategoryEnter, shouldHighlight, hasNestedCategories } = useMultilevelMenuContext();

  return (
    <Fragment>
      {block?.content?.map(({ contentLink }: { contentLink: ContentLink }) => {
        const code = contentLink?.expanded?.code;
        const displayName = contentLink?.expanded?.displayName;
        const url = contentLink?.expanded?.url;

        return (
          <div key={code} onMouseEnter={() => handleCategoryEnter(code)}>
            <SmartLink
              href={url}
              sx={{
                '& .link-text': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: colors.text.primary,
                  backgroundColor: shouldHighlight(code)
                    ? colors.primary.lightGreen
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: colors.primary.lightGreen,
                  },
                },
              }}
            >
              <ListItemText
                primary={displayName}
                sx={{
                  px: 6,
                  cursor: 'pointer',
                  flex: 'none',
                  minWidth: '250px',
                }}
              />
              {hasNestedCategories(code) ? <ChevronRightIcon sx={{ mr: 2 }} /> : null}
            </SmartLink>
          </div>
        );
      })}
    </Fragment>
  );
};
